import { useState } from "react"
import { motion, AnimatePresence, MotionConfig } from "framer-motion"
import Button from "./Button"

interface navProps {
	notHomepage: boolean
}

export default function Nav({ notHomepage }: navProps) {
	const [mobileNav, setMobileNav] = useState(false)

	const toggleMobileNav = () => {
		setMobileNav(!mobileNav)
	}

	const about = "#About"
	const portfolio = "#Portfolio"
	const process = "#Process"
	const pricing = "#pricing"
	const contact = "#Contact"

	return (
		<div className="absolute top-0 inset-x-0 bg-transparent z-50">
			<div
				className={`w-screen bg-black transition-all duration-300 ${
					mobileNav
						? "opacity-60 pointer-events-auto"
						: "opacity-0 pointer-events-none"
				}`}
			></div>

			<div
				className={`lg:px-24 px-8 2xl:px-24 flex flex-row w-full justify-between items-center lg:items-start z-40 transition-all duration-300 ${"text-white bg-transparent lg:pt-16 pt-8"} `}
			>
				{/* Logo Section */}
				<a href="/">
					<div
						className={`flex flex-row items-center font-medium gap-2 select-none z-50 transition-all duration-75 ${"lg:text-3xl text-2xl"} `}
					>
						<img
							className="lg:h-auto h-8 w-8 transition-all duration-75"
							width={50}
							height={50}
							src="/dot.png"
							alt=""
						/>
						<span
							className={`
								${"text-white"} transition-all`}
						>
							Gamefizz
						</span>
					</div>
				</a>

				{/* Hamburger Menu for Mobile */}

				{!notHomepage ? (
					<div className="lg:hidden">
						<nav className="container mx-auto">
							{/* Hamburger Button */}
							<motion.button
								initial="hide"
								animate={mobileNav ? "show" : "hide"}
								onClick={toggleMobileNav}
								className="flex flex-col space-y-1 relative z-10"
							>
								<motion.span
									variants={{
										hide: { rotate: 0 },
										show: { rotate: 45, y: 6 },
									}}
									className={`w-6 ${
										mobileNav ? "bg-black" : `${"bg-white"}`
									} h-[2px] block`}
								></motion.span>
								<motion.span
									variants={{
										hide: { opacity: 1 },
										show: { opacity: 0 },
									}}
									className={`w-6 ${
										mobileNav ? "bg-black" : `${"bg-white"}`
									} h-[2px] block`}
								></motion.span>
								<motion.span
									variants={{
										hide: { rotate: 0 },
										show: { rotate: -45, y: -6 },
									}}
									className={`w-6 ${
										mobileNav ? "bg-black" : `${"bg-white"}`
									} h-[2px] block`}
								></motion.span>
							</motion.button>

							{/* Mobile Navigation Menu */}
							<AnimatePresence>
								{mobileNav && (
									<MotionConfig
										transition={{
											type: "spring",
											bounce: 0.05,
											duration: 0.5,
										}}
									>
										<motion.div
											key="mobile-nav"
											variants={{
												hide: {
													x: "100%",
													transition: {
														type: "spring",
														bounce: 0.05,
														staggerChildren: 0.03,
													},
												},
												show: {
													x: "0%",
													transition: {
														type: "spring",
														bounce: 0.05,
														staggerChildren: 0.03,
													},
												},
											}}
											initial="hide"
											animate="show"
											exit="hide"
											className="fixed inset-0 bg-white text-black p-12 flex flex-col justify-center space-y-10 lg:hidden"
										>
											{/* Links */}
											<motion.ul
												variants={{
													hide: { y: "25%", opacity: 0 },
													show: { y: "0%", opacity: 1 },
												}}
												className="list-none space-y-3"
											>
												<li>
													<a
														href={about}
														className="text-4xl "
														onClick={() => setMobileNav(false)}
													>
														ABOUT
													</a>
												</li>
												<li>
													<a
														href={portfolio}
														className="text-4xl"
														onClick={() => setMobileNav(false)}
													>
														PORTFOLIO
													</a>
												</li>
												<li>
													<a
														href={process}
														className="text-4xl"
														onClick={() => setMobileNav(false)}
													>
														PROCESS
													</a>
												</li>
											<li>
													<a
														href={pricing}
														className="text-4xl"
														onClick={() => setMobileNav(false)}
													>
														PRICING
													</a>
												</li>
												<li>
													<a
														href={contact}
														className="text-4xl"
														onClick={() => setMobileNav(false)}
													>
														CONTACT
													</a>
												</li>
											</motion.ul>

											{/* Divider */}
											<motion.div
												variants={{
													hide: { y: "25%", opacity: 0 },
													show: { y: "0%", opacity: 1 },
												}}
												className="w-full h-px bg-white/30"
											></motion.div>

											{/* Social Icons */}
											<motion.ul
												variants={{
													hide: { y: "25%", opacity: 0 },
													show: { y: "0%", opacity: 1 },
												}}
												className="list-none flex"
											>
												<div className="flex gap-6 flex-col">
													<Button
														link="https://discord.gg/sy5k53rkz4"
														theme="white-bg"
													>
														Gamefizz Discord
													</Button>
													<Button
														link="mailto:contact@gamefizz.gg"
														theme="white-bg"
													>
														contact@gamefizz.gg
													</Button>
												</div>
											</motion.ul>
										</motion.div>
									</MotionConfig>
								)}
							</AnimatePresence>
						</nav>
					</div>
				) : (
					""
				)}

				{/* Navigation Links */}

				{!notHomepage ? (
					<div className="lg:opacity-100 opacity-0 lg:relative absolute">
						<ul className="flex flex-col items-end pointer-events-none lg:pointer-events-auto">
							{[
								{ label: "About", href: "#About" },
								{ label: "Portfolio", href: "#Portfolio" },
								{ label: "Process", href: "#Process" },
								{ label: "Pricing", href: "#pricing" },
								{ label: "Contact", href: "#Contact" },
							].map(
								(item) => (
									<li key={item.label}>
										<a
											href={item.href}
											className="transition-transform transform-gpu scale-100 hover:scale-125 text-2xl inline-block duration-100"
										>
											{item.label.toUpperCase()}
										</a>
									</li>
								)
							)}
						</ul>
					</div>
				) : (
					""
				)}
			</div>
		</div>
	)
}
